/**
 * gapToPrompt.ts — Readiness gaps as conversation, not compliance.
 *
 * FEAT-62 (BET-64 Slice 1): Translates raw readiness gap data into
 * natural-language prompts with contextual framing. The user should
 * hear a collaborator suggesting what to work on — never a checklist.
 *
 * Consumed by orient (health.ts) and start (start.ts).
 * Source prompts come from collection.readinessGuidance (DB).
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface ReadinessGap {
  id: string;
  label: string;
  guidance: string;
  capabilityGuidance?: string;
  canonicalKey?: string;
  current: number;
  required: number;
}

export interface GapContext {
  stage: string;
  passedChecks: number;
  totalChecks: number;
  score: number;
}

// ── Label cleanup ────────────────────────────────────────────────────────────
// Raw gap labels come through as "Glossary has entries" or "Strategy — your
// product vision". Strip the mechanical suffixes to get a clean noun.

function cleanLabel(label: string): string {
  return label
    .replace(/ has entries$/i, "")
    .replace(/ coverage$/i, "")
    .replace(/^Strategy — /i, "");
}

// ── Progress framing ─────────────────────────────────────────────────────────
// One sentence that grounds the user in where they are. Warm, not clinical.

function progressFrame(ctx: GapContext): string {
  if (ctx.passedChecks === 0) {
    return "Your Brain is just getting started.";
  }
  if (ctx.passedChecks === 1) {
    return "Your Brain has one area covered so far.";
  }
  if (ctx.score >= 70) {
    return `Your Brain covers ${ctx.passedChecks} areas — almost there.`;
  }
  return `Your Brain covers ${ctx.passedChecks} of ${ctx.totalChecks} areas.`;
}

// ── Single gap prompt ────────────────────────────────────────────────────────

/**
 * Format the most impactful gap as a conversational next-step prompt.
 * Used in orient (low-readiness) and start (orient-style) responses.
 */
export function formatTopGapPrompt(
  gap: ReadinessGap,
  remaining: number,
  ctx: GapContext,
): string {
  const prompt = gap.capabilityGuidance ?? gap.guidance;
  const label = cleanLabel(gap.label);

  const lines = [
    progressFrame(ctx),
    remaining > 0
      ? `The biggest gap right now: **${label}**.`
      : `One area would strengthen it: **${label}**.`,
    "",
    prompt,
  ];

  return lines.join("\n");
}

// ── Multi-gap list ───────────────────────────────────────────────────────────

/**
 * Format multiple gaps as natural suggestions (not a checklist).
 * Used in start seeded response and orient full mode.
 */
export function formatGapList(
  gaps: ReadinessGap[],
  limit = 3,
): string[] {
  const topGaps = gaps.slice(0, limit);
  if (topGaps.length === 0) return [];

  const lines: string[] = [
    "Here's where your Brain would benefit most:",
    "",
  ];

  for (let i = 0; i < topGaps.length; i++) {
    const gap = topGaps[i];
    const prompt = gap.capabilityGuidance ?? gap.guidance;
    const label = cleanLabel(gap.label);
    lines.push(`${i + 1}. **${label}** — ${prompt}`);
  }

  return lines;
}

// ── Brief-mode one-liner ─────────────────────────────────────────────────────

/**
 * One-line gap summary for brief orient mode.
 * Returns null if no gaps.
 */
export function formatGapOneLiner(gap: ReadinessGap | undefined): string | null {
  if (!gap) return null;
  const prompt = gap.capabilityGuidance ?? gap.guidance;
  return `Next gap: ${prompt}`;
}
